/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
(() => {
  class QuantitySelectorAndy extends HTMLElement {
    constructor() {
      super();
      this.minus = null;
      this.plus = null;
      this.display = null;
      this.quantityState = {
        id: null,
        quantity: 0,
        hasCorrectQuantity: true,
        message: "",
      };
    }

    connectedCallback() {
      this.initializeElements();
      this.initializeState();
      this.attachEventListeners();
    }

    // Initialize elements
    initializeElements() {
      this.minus = this.querySelector(".js-minus-qty-selector");
      this.plus = this.querySelector(".js-plus-qty-selector");
      this.display = this.querySelector(".js-qty-input");

      if (!this.minus || !this.plus || !this.display) {
        console.error("QuantitySelector requires .js-minus-qty-selector, .js-plus-qty-selector, and .js-qty-input.");
      }
    }

    // Initialize the state
    initializeState() {
      const variantId = this.getAttribute("data-variant-id");
      if (!variantId) {
        console.error("data-variant-id attribute is missing on QuantitySelector.");
        return;
      }

      this.quantityState.id = variantId;
      this.quantityState.quantity = parseInt(this.display.value, 10);

      // Disable the minus button if the quantity is at its minimum
      this.updateMinusButtonState();
    }

    // Attach all event listeners
    attachEventListeners() {
      this.minus.addEventListener("click", this.handleMinusClick.bind(this));
      this.plus.addEventListener("click", this.handlePlusClick.bind(this));
      this.display.addEventListener("change", this.handleInputChange.bind(this));
    }

    // Handle Minus Button Click
    handleMinusClick() {
      let amount = parseInt(this.display.value, 10); // Get the current quantity
      const step = parseInt(this.display.step, 10); // Get the step value
      const min = parseInt(this.display.min, 10); // Get the minimum quantity

      // Prevent decrement if amount - step is below 1 (guard clause)
      if (amount - step < 1) {
        return;
      }

      // Decrement amount
      amount -= step;

      // Check if the decremented amount meets the min value
      if (amount >= min) {
        this.display.value = amount; // Update the display
        this.updateMinusButtonState(); // Update minus button accessibility
        this.quantityState.quantity = amount; // Update state
        this.quantityState.hasCorrectQuantity = true; // State is valid
        this.dispatchQuantityChangeEvent(); // Dispatch event
      } else {
        this.display.value = min; // Set value to min
        this.updateMinusButtonState();
        this.quantityState.quantity = min; // Clamp state quantity to min
        this.quantityState.hasCorrectQuantity = false; // Mark quantity as invalid
        this.quantityState.message = window.quickOrderListStrings.min_error.replace("[min]", min); // Generate error message
        this.dispatchQuantityChangeEvent(); // Dispatch event
      }
    }

    // Handle Plus Button Click
    handlePlusClick() {
      let amount = parseInt(this.display.value, 10); // Get current value
      const step = parseInt(this.display.step, 10) || 1; // Default step to 1 if not provided
      const max = parseInt(this.display.max, 10) || 0; // Default max to 0 (no limit)

      // Increment the amount
      amount += step;

      // Check if the incremented amount exceeds max, but allow unlimited if max is 0
      if (max > 0 && amount > max) {
        // Exceeds max, reset to max
        amount = max;
        this.quantityState.hasCorrectQuantity = false; // Validation fails
        this.quantityState.message = window.quickOrderListStrings.max_error.replace("[max]", max); // Error message
      } else {
        // Within bounds, clear validation errors
        this.quantityState.hasCorrectQuantity = true;
        this.quantityState.message = ""; // No error message
      }
      // Update the quantity and state
      this.display.value = amount; // Set the input value
      this.quantityState.quantity = amount; // Update state quantity
      this.updateMinusButtonState(); // Update minus button status
      this.dispatchQuantityChangeEvent(); // Dispatch the changeQuantity event
    }

    // Handle Input Change (manual user input)
    handleInputChange() {
      let amount = parseInt(this.display.value, 10); // Parse input value as integer
      const min = parseInt(this.display.min, 10) || 1; // Default minimum to 1
      const max = parseInt(this.display.max, 10) || 0; // Default max to 0 (no limit)
      const step = parseInt(this.display.step, 10) || 1; // Default step to 1

      // Validate min and max constraints
      if (amount < min) {
        // Clamp to minimum value
        amount = min;
        this.quantityState.quantity = amount;
        this.quantityState.hasCorrectQuantity = false;
        this.quantityState.message =
          window.quickOrderListStrings?.min_error?.replace("[min]", min) || `Minimum quantity is ${min}.`;
        this.setAttribute("data-quantity-clamped", "true");
      } else if (max > 0 && amount > max) {
        // Clamp to maximum value
        amount = max;
        this.quantityState.quantity = amount;
        this.quantityState.hasCorrectQuantity = false;
        this.quantityState.message =
          window.quickOrderListStrings?.max_error?.replace("[max]", max) || `Maximum quantity is ${max}.`;
        this.setAttribute("data-quantity-clamped", "true");
      } else if (amount % step !== 0) {
        // Adjust to nearest step and validate
        amount = Math.round(amount / step) * step; // Snap to the nearest step
        amount = Math.max(min, Math.min(amount, max)); // Ensure value stays within min and max
        this.quantityState.quantity = amount;
        this.quantityState.hasCorrectQuantity = false;
        this.quantityState.message =
          window.quickOrderListStrings?.step_error?.replace("[step]", step) ||
          `Quantity must be a multiple of ${step}.`;
        this.setAttribute("data-quantity-clamped", "true");
      } else {
        // Valid quantity
        this.quantityState.quantity = amount;
        this.quantityState.hasCorrectQuantity = true;
        this.quantityState.message = ""; // Clear error message
        this.setAttribute("data-quantity-clamped", "false");
      }

      // Update the input value, UI state, and dispatch changeQuantity event
      this.display.value = amount; // Update the input field
      this.updateMinusButtonState(); // Update minus button state
      this.dispatchQuantityChangeEvent(); // Dispatch the changeQuantity event
    }

    // Update the state of the minus button
    updateMinusButtonState() {
      const amount = parseInt(this.display.value, 10);
      const min = parseInt(this.display.min, 10);
      this.minus.disabled = amount <= min;
    }

    // Dispatch the changeQuantity event
    dispatchQuantityChangeEvent() {
      const event = new CustomEvent("changeQuantity", {
        bubbles: true,
        detail: {
          state: this.quantityState,
        },
      });
      this.dispatchEvent(event);
      this.quantityState.hasCorrectQuantity = true;
    }
  }

  // Register the custom element
  if (!customElements.get("quantity-selector")) {
    customElements.define("quantity-selector", QuantitySelectorAndy);
  }
})();
